import {
  ApiDistributionAlert,
  ApiDistributionData,
  ApiDistributionType,
  ApiNewReinvestmentConfirmation,
  ApiReinvestmentConfirmation
} from '../../../api/_legacy/generated-api'
import { ExDateDistribution, ExDateReinvestment } from '../../../components/core-v2'
import { formatToServerDateFromStr, isPastDate } from '../../../utilities/date'
import { ExDateSchedule, ExDateStatus } from './exDateSchedule'

const createAbsaApiDistribution = (
  assetId: string,
  exDate: string,
  timeNow: string,
  distributionType: ApiDistributionType,
  amount: number
): ApiDistributionData => ({
  code: assetId,
  receivedTime: timeNow,
  declarationDate: null,
  reinvestmentDate: null,
  isCorrection: false,
  distributionType,
  exDistributionDate: formatToServerDateFromStr(exDate),
  amountPerShare: amount,
  providerType: 'Absa'
})

export const updateScheduleReinvestments = (
  exDateSchedules: ExDateSchedule[],
  mappedReinvestments: ApiReinvestmentConfirmation[]
) => {
  exDateSchedules.forEach(clonedExDateSchedule => {
    const matchingReinvestments = mappedReinvestments.filter(
      item => item.exDate === clonedExDateSchedule.dateStr
    )
    let hasAnyAbsaReinvestment = false
    if (matchingReinvestments && matchingReinvestments.length > 0) {
      matchingReinvestments.forEach(matchingReinvestment => {
        const isFinSwitchDistribution = matchingReinvestment.tradingAgent === 'FinSwitch'
        if (!isFinSwitchDistribution && !hasAnyAbsaReinvestment) {
          hasAnyAbsaReinvestment = matchingReinvestment.tradingAgent === 'Absa'
        }
        const matchedReinvestmentValue = {
          readOnly: isFinSwitchDistribution,
          numberOfUnits: matchingReinvestment.shares,
          reinvestmentDate: matchingReinvestment.reinvestmentDate
        }
        if (isFinSwitchDistribution) {
          clonedExDateSchedule.finSwitchReinvestment = matchedReinvestmentValue
          clonedExDateSchedule.reinvestmentStatus = ExDateStatus.FinSwitchReceived
        } else {
          clonedExDateSchedule.finalReinvestment = matchedReinvestmentValue
          clonedExDateSchedule.reinvestmentStatus = ExDateStatus.UserSubmitted
        }
      })
    }
    if (!hasAnyAbsaReinvestment) {
      // Absa should be overriden with what we got from finswitch, if no value from absa
      if (clonedExDateSchedule.finSwitchReinvestment.reinvestmentDate) {
        clonedExDateSchedule.finalReinvestment.reinvestmentDate =
          clonedExDateSchedule.finSwitchReinvestment.reinvestmentDate
        clonedExDateSchedule.finalReinvestment.numberOfUnits =
          clonedExDateSchedule.finSwitchReinvestment.numberOfUnits
      }
    } else {
      clonedExDateSchedule.finalReinvestment.readOnly = true
      clonedExDateSchedule.reinvestmentStatus = ExDateStatus.UserSubmitted
    }
  })
  return exDateSchedules
}

export const convertToApiDistribution = (
  assetId: string,
  exDate: string,
  distribution: ExDateDistribution
): ApiDistributionData[] => {
  const result: ApiDistributionData[] = []
  const timeNow = new Date().toUTCString()
  if (distribution.capitalPerShare != null) {
    result.push(
      createAbsaApiDistribution(assetId, exDate, timeNow, 'Capital', distribution.capitalPerShare)
    )
  }
  if (distribution.dividendPerShare != null) {
    result.push(
      createAbsaApiDistribution(assetId, exDate, timeNow, 'Dividend', distribution.dividendPerShare)
    )
  }
  if (distribution.interestPerShare != null) {
    result.push(
      createAbsaApiDistribution(assetId, exDate, timeNow, 'Interest', distribution.interestPerShare)
    )
  }

  return result
}

export const convertToApiReinvestment = (
  assetId: string,
  exDate: string,
  distribution: ExDateReinvestment
): ApiNewReinvestmentConfirmation => {
  const result: ApiNewReinvestmentConfirmation = {
    assetId,
    shares: distribution.numberOfUnits,
    reinvestmentDate: formatToServerDateFromStr(distribution.reinvestmentDate),
    exDate
  }
  return result
}

export const isPreviousExDate = (exDateSchedule: ExDateSchedule) =>
  isPastDate(exDateSchedule.dateStr) &&
  exDateSchedule.distributionStatus === ExDateStatus.UserSubmitted &&
  exDateSchedule.reinvestmentStatus === ExDateStatus.UserSubmitted

const AlertNames = {
  DistributionLate: 'LATE DISTRIBUTION DATA',
  ConfirmationLate: 'LATE CONFIRMATION DATA',
  DistributionReceived: 'DISTRIBUTION DATA RECEIVED',
  DistributionSubmitted: 'DISTRIBUTION DATA SUBMITTED',
  ConfirmationReceived: 'REINVESTMENT DATA RECEIVED',
  ConfirmationSubmitted: 'REINVESTMENT DATA SUBMITTED'
}

export const getExDateAlerts = (exDateSchedule: ExDateSchedule): string[] => {
  const alerts = []

  if (exDateSchedule.distributionStatus === ExDateStatus.UserSubmitted) {
    alerts.push(AlertNames.DistributionSubmitted)
  } else if (exDateSchedule.distributionStatus === ExDateStatus.FinSwitchReceived) {
    alerts.push(AlertNames.DistributionReceived)
  }

  if (exDateSchedule.reinvestmentStatus === ExDateStatus.UserSubmitted) {
    alerts.push(AlertNames.ConfirmationSubmitted)
  } else if (exDateSchedule.reinvestmentStatus === ExDateStatus.FinSwitchReceived) {
    alerts.push(AlertNames.ConfirmationSubmitted)
  }
  return alerts
}

export const getExDateAlertsFromApiFlags = (
  distributionAlertFlags: ApiDistributionAlert
): string[] => {
  const alerts = []

  if (distributionAlertFlags) {
    if (distributionAlertFlags.isDistributionLate) {
      alerts.push(AlertNames.DistributionLate)
    }
    if (distributionAlertFlags.isConfirmationLate) {
      alerts.push(AlertNames.ConfirmationLate)
    }
    if (distributionAlertFlags.hasOnlyProviderDistribution) {
      alerts.push(AlertNames.DistributionReceived)
    }
    if (
      distributionAlertFlags.hasOnlyProviderConfirmation ||
      distributionAlertFlags.hasUnassignedConfirmation
    ) {
      alerts.push(AlertNames.ConfirmationReceived)
    }
  }
  return alerts
}
